import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import Button from 'zignal-landscape/lib/Button';
import moment from 'moment';
import get from 'lodash/get';
import noop from 'lodash/noop';
import map from 'lodash/map';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';

import Loading from 'zignal-landscape/lib/Loading';
import {
  TABLE,
  THEAD,
  TBODY,
  TH,
  TR,
  TD,
} from 'zignal-landscape/lib/Table';
import Banner from 'zignal-landscape/lib/Banner';
import Col from 'zignal-landscape/lib/Col';
import GridContent from 'zignal-landscape/lib/GridContent';
import Row from 'zignal-landscape/lib/Row';

const DEFAULT_TWITTER_IMPORTS = [];

const TwitterImportsTable = ({
  twitterImports = DEFAULT_TWITTER_IMPORTS,
  onLoadMore = noop,
  hasMore = false,
  styles,
  accept,
  onCleanError,
  errorOnChangeStatus,
  timezone
}) => {
  const loader = (
    <div key="TwitterLoader" style={{ textAlign: 'center', padding: '40px', position: 'relative', height: '80px' }}>
      <Loading />
    </div>
  );
  const [allowImport, setAllowImport] = useState(true);
  const [messageType, setMessageType] = useState('error');
  const [errorMessageTitle, setErrorMessageTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const ErrorMessageRow = () => (
    <Row>
      <Col md={6} mdOffset={3}>
        <GridContent>
          <Banner
            bannerStyle="inline"
            bannerType={messageType}
            onClick={allowImport ? onCleanError : () => setAllowImport(true)}
            title={allowImport ? 'Error' : errorMessageTitle}
          >
            {allowImport ? errorOnChangeStatus.reason : errorMessage}
          </Banner>
        </GridContent>
      </Col>
    </Row>
  );
  const twitterImportRows = map(twitterImports, (twitterImport) => {
    const {
      id,
      campaign_id,
      story_count: storiesFound,
      profiles,
      start_time,
      end_time,
      status,
      percentComplete,
      progress,
      quote,
    } = twitterImport;

    const title = <div>{campaign_id}</div>;

    const profilesList = map(profiles, (name) => {
      return React.Children.toArray(<div>{name}</div>);
    });

    const start_timeFormatted = moment.tz(start_time, timezone).format('MM/DD/YYYY h:mma');
    const end_timeFormatted = moment.tz(end_time, timezone).format('MM/DD/YYYY h:mma');
    const storiesEstimated = get(quote, 'estimatedActivityCount');

    const getPercetageValue = (withPercentComplete) => {
      const progressCompleteValue = withPercentComplete ? percentComplete: '';
      return isNumber(progress) ? progress : progressCompleteValue;
    };
    // If status is loading, Do n't display percentComplete(ES stories are not ready, yet).
    // Only progress is should be displayed which is calculated correctly for loading status.

    const progressValue = status && status.toLowerCase() === 'loading' ?
      getPercetageValue(false)
      : getPercetageValue(true);

    const progressFormatted = status === 'quoted' ? '' : isNumber(progressValue) && `${progressValue}%`;

    const importClickAction = () => {
      let allowedConcurrencyImportJobs = 1;
      const concurrentImportJobs = twitterImports.filter(
        (x) => x.type?.toLowerCase() === 'import' &&
          (x.status?.toLowerCase() === 'running' || x.status?.toLowerCase() === 'accepted')
      );

      if (window.location.host.includes('client.zignallabs.com')) {
        allowedConcurrencyImportJobs = 2;
      }

      if (concurrentImportJobs.length >= allowedConcurrencyImportJobs) {
        setMessageType('info');
        setErrorMessageTitle('Maximum number of Imports Reached');
        setErrorMessage(`We are currently running the maximum number of concurrent imports allowed.
          Please try again later.`);
        setAllowImport(false);
        return;
      }

      if (isNumber(storiesEstimated) && storiesEstimated > 20000000) {
        setMessageType('error');
        setErrorMessageTitle('This import is too large');
        setErrorMessage(`This import is larger than 20M stories.
          If this import is significantly larger than 20M stories
          e.g. 50M, please contact Product, otherwise,
          you can break it down into smaller imports`);
        setAllowImport(false);
        return;
      }

      accept(id, campaign_id, [campaign_id], start_time);
    };

    return (
      <TR key={id} id={id}>
        <TD>{id}</TD>
        <TD>{title}</TD>
        <TD>{profilesList}</TD>
        <TD>{start_timeFormatted} - {end_timeFormatted}</TD>
        <TD>
          <div>{storiesFound}</div>
        </TD>
        <TD style={{ textTransform: 'capitalize' }}>{status}</TD>
        <TD>{progressFormatted}</TD>
      </TR>
    );
  });

  const zoneAbbr = moment.tz(timezone).zoneAbbr();

  return !!twitterImports.length &&
    <div className={styles.TwitterImportsTableWrapper}>
      {(errorOnChangeStatus || allowImport === false) && <ErrorMessageRow />}
      <InfiniteScroll
        pageStart={0}
        loadMore={onLoadMore}
        hasMore={hasMore}
        useWindow={false}
        loader={loader}
        initialLoad={false}
      >
        <TABLE>
          <THEAD>
            <TR>
              <TH>Job Id</TH>
              <TH>Workspaces</TH>
              <TH>Datasets</TH>
              <TH>Date Range ({zoneAbbr})</TH>
              <TH>Stories</TH>
              <TH>Status</TH>
              <TH>Percent Complete</TH>
            </TR>
          </THEAD>
          <TBODY>
            {twitterImportRows}
          </TBODY>
        </TABLE>
      </InfiniteScroll>
    </div>;
};

TwitterImportsTable.propTypes = {
  styles: PropTypes.object.isRequired,
  twitterImports: PropTypes.array,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  accept: PropTypes.func,
  onCleanError: PropTypes.func,
  errorMessage: PropTypes.string,
  errorOnChangeStatus: PropTypes.object,
  timezone: PropTypes.string
};

export default TwitterImportsTable;
